/* global BOOMR */
/* global newrelic */

const checkPerformanceSupport = () => {
  // Try catch at top level allows this module to be used with SSR.
  try {
    return (
      window &&
      window.performance &&
      typeof window.performance.getEntriesByName === 'function' &&
      typeof window.performance.mark === 'function' &&
      typeof window.performance.measure === 'function'
    )
  } catch (e) {
    console.error(e)
    return false
  }
}
/*
 * Append event to boomerang
 *
 */
export const sendToBoomerang = (event, value, eventType) => {
  const isPerformanceAPI = checkPerformanceSupport()

  if (window.BOOMR) {
    if (value) {
      BOOMR.addVar(event, value)
    } else if (isPerformanceAPI) {
      const entries = window.performance.getEntriesByName(event)
      if (entries && entries.length > 0) {
        if (eventType && eventType === 'measure') {
          // if event type is measure, log the duration relative to the mark(s)
          BOOMR.addVar(event, entries[0].duration)
        } else {
          // else (if type is mark), log the startTime
          BOOMR.addVar(event, entries[0].startTime)
        }
      }
    }
  } else {
    document.addEventListener('onBoomerangLoaded', (e) => {
      if (e && e.detail && e.detail.BOOMR) {
        const BOOMR = e.detail.BOOMR
        if (value) {
          BOOMR.addVar(event, value)
        } else if (isPerformanceAPI) {
          const entries = window.performance.getEntriesByName(event)
          if (entries && entries.length > 0) {
            if (eventType && eventType === 'measure') {
              // always add the first entry added to the array
              BOOMR.addVar(event, entries[0].duration)
            } else {
              // always add the first entry added to the array
              BOOMR.addVar(event, entries[0].startTime)
            }
          }
        }
      }
    })
  }
}

/*
 * Append event to newrelic
 *
 */
export const sendToNewRelic = (event, value, eventType) => {
  if (typeof newrelic == 'object') {
    if (value) {
      newrelic.setCustomAttribute(event, value)
    } else {
      const entries = window.performance.getEntriesByName(event)
      if (entries && entries.length > 0) {
        if (eventType && eventType === 'measure') {
          // always add the first entry added to the array
          newrelic.setCustomAttribute(event, entries[0].duration)
        } else {
          // always add the first entry added to the array
          newrelic.setCustomAttribute(event, entries[0].startTime)
        }
      }
    }
  }
}
/*
 * Add measure entry and append it to boomerang
 *
 * [MDN definition](https://developer.mozilla.org/en-US/docs/Web/API/Performance/mark)
 *
 * The mark() method creates a timestamp in the browser's performance entry buffer
 * with the given name.
 *
 */
export const mark = (eventName, opts) => {
  const isPerformanceAPI = checkPerformanceSupport()

  // mark event in browser user timing API
  if (isPerformanceAPI) {
    try {
      window.performance.mark(eventName)
      sendToBoomerang(eventName, null, 'mark')

      if (typeof opts === 'object' && opts.newrelic) {
        sendToNewRelic(eventName, null, 'mark')
      }
    } catch (err) {
      console.error(err)
    }
  }
}

/*
 * Add measure entry and append it to boomerang
 *
 * [MDN definition](https://developer.mozilla.org/en-US/docs/Web/API/Performance/measure)
 *
 * The measure() method creates a named timestamp in the browser's performance
 * entry buffer between marks, the navigation start time, or the current time.
 * When measuring between two marks, there is a start mark and end mark, respectively.
 * The named timestamp is referred to as a measure.
 *
 */
export const measure = (eventName, startMark, endMark, opts) => {
  const isPerformanceAPI = checkPerformanceSupport()

  // mark event in browser user timing API
  if (isPerformanceAPI) {
    try {
      window.performance.measure(eventName, startMark, endMark)
      sendToBoomerang(eventName, null, 'measure')

      if (typeof opts === 'object' && opts.newrelic) {
        sendToNewRelic(eventName, null, 'measure')
      }
    } catch (err) {
      console.error(err)
    }
  }
}

export const markInteractionStart = (name) => {
  if (window.BOOMR && typeof BOOMR !== 'undefined') {
    const timer = BOOMR.markInteractionStart(name)
    if (window.BOOMR.markInteractionTimers) {
      window.BOOMR.markInteractionTimers[name] = timer
    }
    return timer
  }
}

export const markInteractionEnd = (name, data) => {
  if (window.BOOMR && typeof BOOMR !== 'undefined' && window.BOOMR.markInteractionTimers) {
    const timer = window.BOOMR.markInteractionTimers[name]
    if (timer && typeof timer.markInteractionEnd === 'function') {
      timer.markInteractionEnd({
        pl_vph: window.innerHeight || document.body.clientHeight || 0,
        pl_vpw: window.innerWidth || document.body.clientWidth || 0,
        ...data,
      })
    }
  }
}

/*
 * Add a tag to the boomerang event,
 * useful for adding SETI names and their variants
 *
 */
export const tag = (eventName, value, opts) => {
  sendToBoomerang(eventName, value)

  if (typeof opts === 'object' && opts.newrelic) {
    sendToNewRelic(eventName, value)
  }
}
